import React, { useEffect, useState } from 'react';
import { useQueryClient, useMutation, useIsFetching, useQuery } from '@tanstack/react-query';
import {
  ClipboardList, CheckCircle, User, Plus, Edit2, Trash2, X, Check,
  Loader2, CirclePlay, GripVertical, Edit, Trash, AlertCircle
} from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { workOrdersService } from '../../services/workOrdersService';
import { maintPlanService } from '../../services/maintPlanService';
import { documentService } from '../../../../services/documentos/documentService';
import { ExecutionModal } from './ExecutionModal';
import { createPortal } from 'react-dom';

export const ExecutionTasks = ({ workOrder, dateFormat, onFinish, isSuperior, userId /*onTaskStart*/ }) => {
  const queryClient = useQueryClient();
  const workOrderId = workOrder?.id;
  const maintenancePlanId = workOrder?.maintenance_plan_id;

  const tasks = [...(workOrder?.tasks || [])].sort((a, b) => a.task_number - b.task_number);
  const [isAdding, setIsAdding] = useState(false);
  const [newTaskDesc, setNewTaskDesc] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskDesc, setEditTaskDesc] = useState('');
  const [editTaskAssignee, setEditTaskAssignee] = useState('');
  const [submittingTaskId, setSubmittingTaskId] = useState(null);
  const [currentProcedureIndex, setCurrentProcedureIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedAssig, setSelectedAssig] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch the maintenance plan to get procedures
  const {
    data: planData,
    isLoading: isLoadingPlan,
    isError: isErrorPlan,
    error: planError,
  } = useQuery({
    queryKey: ['maintenancePlan', maintenancePlanId],
    queryFn: () => maintPlanService.getById(maintenancePlanId),
    enabled: !!maintenancePlanId,
    retry: 1,
  });

  const procedures = planData?.procedures || [];
  const spareParts = planData?.required_spare_parts || [];

  const { data: documentFeatures } = useQuery({
    queryKey: ['documentFeatures'],
    queryFn: () => documentService.getDocumentFeatures(),
    enabled: !!isModalOpen,
  });
  const documentTypes = documentFeatures?.document_types ?? [];
  const assigneeOptions = workOrder?.assignments || [];

  const nextTaskNumber = tasks.length + 1;

  const invalidateWorkOrders = () => {
    queryClient.invalidateQueries({ queryKey: ['wo'] });
  };

  const addTaskMutation = useMutation({
    mutationFn: (data) => workOrdersService.addTask(workOrderId, data),
    onSuccess: () => {
      invalidateWorkOrders();
      setIsAdding(false);
      setNewTaskDesc('');
      setNewTaskAssignee('');
      setIsSubmitting(false);
    },
    onError: (err) => {
      console.error('Failed to add task:', err);
      setIsSubmitting(false);
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, data }) => workOrdersService.updateTask(taskId, data),
    onSuccess: () => {
      invalidateWorkOrders();

    },
    onError: (err) => console.error('Failed to update task:', err),
  });

  const updateAssigMutation = useMutation({
    mutationFn: ({ assigId, data }) => workOrdersService.updateAssig(assigId, data),
    onError: (err) => console.error('Failed to update assignment:', err),
  });

  const updateAssigExternalMutation = useMutation({
    mutationFn: ({ assigId, data }) => workOrdersService.updateExtAssig(assigId, data),
    onError: (err) => console.error('Failed to update external assignment:', err),
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId) => workOrdersService.deleteTask(taskId),
    onSuccess: () => invalidateWorkOrders(),
  });

  const handleAddTask = () => {
    if (!newTaskDesc.trim()) return;
    addTaskMutation.mutate({
      task_number: nextTaskNumber,
      description: newTaskDesc.trim(),
      assigned_to: newTaskAssignee ? parseInt(newTaskAssignee) : null,
      status: 'pending',
    });
  };

  const handleAddFormSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    handleAddTask();
  };

  const handleUpdateTask = (taskId, updatedFields) => {
    const backendData = {};
    if (updatedFields.description !== undefined) backendData.description = updatedFields.description;
    if (updatedFields.assignee_id !== undefined) backendData.assigned_to = parseInt(updatedFields.assignee_id) || null;
    if (updatedFields.status !== undefined) backendData.status = updatedFields.status;
    if (updatedFields.seconds_worked !== undefined) backendData.seconds_worked = updatedFields.seconds_worked;
    if (updatedFields.restart_at !== undefined) backendData.restart_at = updatedFields.restart_at;
    if (updatedFields.observations !== undefined) backendData.observations = updatedFields.observations;
    if (updatedFields.pause_reasons !== undefined) backendData.pause_reasons = updatedFields.pause_reasons;
    return updateTaskMutation.mutateAsync({ taskId, data: backendData });
  };


  const handleUpdateAssig = (assigId, updatedFields) => {
    const backendData = {};
    if (updatedFields.start_time !== undefined) backendData.start_time = updatedFields.start_time;
    if (updatedFields.end_time !== undefined) backendData.end_time = updatedFields.end_time;
    if (updatedFields.hours_worked !== undefined) backendData.hours_worked = updatedFields.hours_worked;
    return updateAssigMutation.mutateAsync({ assigId, data: updatedFields });
  };

  const handleUpdateExtAssig = (assigId, updatedFields) => {
    const backendData = {};
    if (updatedFields.start_time !== undefined) backendData.start_time = updatedFields.start_time;
    if (updatedFields.end_time !== undefined) backendData.end_time = updatedFields.end_time;
    if (updatedFields.hours_worked !== undefined) backendData.hours_worked = updatedFields.hours_worked;
    return updateAssigExternalMutation.mutateAsync({ assigId, data: updatedFields });
  };

  const handleEditFormSubmit = (e, taskId) => {
    e.preventDefault();
    handleUpdateTask(taskId, {
      description: editTaskDesc,
      assignee_id: editTaskAssignee,
    });
    setEditingTaskId(null);
  };

  const handleDeleteTask = (taskId) => {
    if (window.confirm('Delete this task?')) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  const handleStartTask = async (task) => {
    const timestamp_now = new Date();
    try {
      if (task.status !== 'pending') return;
      setSelectedTask(task);
      let assig;
      if (task.is_external) {
        assig = workOrder?.assignments_external?.find(assig => assig?.employee_id === task.assigned_to);
      } else {
        assig = workOrder?.assignments?.find(assig => assig?.employee_id === task.assigned_to);
      }
      if (workOrder?.tasks?.find(t => t.status !== 'pending')) {
        await handleUpdateTask(task.id, { status: 'in_progress', restart_at: timestamp_now.toISOString() });
        if (assig) {
          if (assig.company_id) {
            await handleUpdateExtAssig(assig.id, { start_time: timestamp_now });
          } else {
            await handleUpdateAssig(assig.id, { start_time: timestamp_now });
          }
        }
      } else {
        await workOrdersService.start(workOrderId);
        await handleUpdateTask(task.id, { status: 'in_progress', restart_at: timestamp_now.toISOString() });
        if (assig) {
          if (assig.company_id) {
            await handleUpdateExtAssig(assig.id, { start_time: timestamp_now });
          } else {
            await handleUpdateAssig(assig.id, { start_time: timestamp_now });
          }
        }
      }
      setSelectedAssig(assig);
      // Patch local task state so the modal gets the correct restart_at immediately,
      // without waiting for the query invalidation refetch to propagate.
      setSelectedTask(prev => ({ ...prev, status: 'in_progress', restart_at: timestamp_now.toISOString(), seconds_worked: 0 }));
    } catch (error) {
      console.error('Error starting task:', error);
    } finally {
      invalidateWorkOrders();
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    const updatedTask = workOrder?.tasks?.find(t => t.id === selectedTask?.id);
    setSelectedTask(updatedTask);
    let updatedAssig;
    if (!updatedTask?.is_external) {
      updatedAssig = workOrder?.assignments?.find(a => a.id === selectedAssig?.id);
    } else {
      updatedAssig = workOrder?.assignments_external?.find(a => a.id === selectedAssig?.id);
    }
    setSelectedAssig(updatedAssig);
  }, [workOrder])


  if (!workOrderId) return null;

  // Render procedure display
  const renderProcedure = (proc) => (
    <div className="bg-gray-700/30 rounded-lg border border-gray-600 overflow-hidden">
      {/* Header row */}
      <div className="flex justify-between gap-4 p-4 bg-gray-700/50 border-b border-gray-600">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-sm text-gray-400 font-mono flex-shrink-0">
            #{proc.step_number}
          </span>
          <h4 className="text-white font-medium break-words">
            {proc.title}
          </h4>
        </div>
        <div className="flex gap-2 items-center">
          <Badge className="bg-blue-900/50 text-blue-200 whitespace-nowrap">
            {proc.expected_duration} min
          </Badge>
        </div>
      </div>

      {/* Description */}
      {proc.description && (
        <div className="p-4 bg-gray-700/30 border-t border-gray-600">
          <p className="text-sm text-gray-400 mb-1">Description</p>
          <p className="text-white text-sm whitespace-pre-wrap break-words">
            {proc.description}
          </p>
        </div>
      )}

      {/* Safety Notes */}
      {proc.safety_notes && (
        <div className="p-4 bg-green-900/20 border-t border-green-800">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-green-400 mb-1">Safety Notes</p>
              <p className="text-white text-sm whitespace-pre-wrap break-words">
                {proc.safety_notes}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {procedures.length !== 0 && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            {/* <ClipboardList className="w-5 h-5 text-gray-400" /> */}
            Procedures
            <Badge className="bg-gray-700 text-gray-300">
              {procedures.length} {procedures.length === 1 ? 'procedure' : 'procedures'}
            </Badge>
          </h3>
        </div>
      )}
      {/* Procedures Carousel */}
      {maintenancePlanId && (
        <div className="space-y-3">
          {isLoadingPlan && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          )}
          {isErrorPlan && (
            <div className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Failed to load procedures
            </div>
          )}
          {!isLoadingPlan && !isErrorPlan && procedures.length > 0 && (
            <>
              {/* Current procedure */}
              {renderProcedure(procedures[currentProcedureIndex])}

              {/* Dots */}
              <div className="flex justify-center gap-2 py-2">
                {procedures.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentProcedureIndex(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${idx === currentProcedureIndex ? 'bg-white' : 'bg-gray-500 hover:bg-gray-400'
                      }`}
                    aria-label={`Go to procedure ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
          {!isLoadingPlan && !isErrorPlan && procedures.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-2">
              No procedures defined for this maintenance plan.
            </div>
          )}
        </div>
      )}

      {/* Tasks Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-gray-400" />
          Tasks
          <Badge className="bg-gray-700 text-gray-300">
            {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
          </Badge>
        </h3>

        {(workOrder.work_order_type === 'corrective' && userId === workOrder.created_by && workOrder.status !== 'completed') && <button
          onClick={() => setIsAdding(true)}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 rounded-lg text-white"
        >
          <Plus className="w-4 h-4" /> Add Task
        </button>}
      </div>

      {/* Task list */}
      <div className="space-y-3">
        {/* Add form */}
        {isAdding && (workOrder.status !== 'completed') && (
          <form onSubmit={handleAddFormSubmit} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
            <div className="flex flex-col gap-2">
              <input
                type="text"
                value={newTaskDesc}
                onChange={(e) => setNewTaskDesc(e.target.value)}
                placeholder="Task description"
                className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600"
                autoFocus
              />
              <select
                value={newTaskAssignee}
                onChange={(e) => setNewTaskAssignee(e.target.value)}
                className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600"
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map((assignment) => (
                  <option key={assignment.employee_id} value={assignment.employee_id}>
                    {assignment.employee_name}
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setIsAdding(false)}
                  className="px-3 py-1 text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button type="submit" className="px-3 py-1 bg-blue-600 rounded-lg hover:bg-blue-700  disabled:opacity-50 disabled:cursor-not-allowed text-white inline-flex items-center gap-2"
                  disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save
                </button>
              </div>
            </div>
          </form>
        )}
        {tasks.map((task) => (
          <div key={task.id} className={`bg-gray-700/30 rounded-lg border border-gray-600 p-4 ${(task.status === 'completed') || (task.status !== 'pending' && (isSuperior || task.assigned_to === userId)) ? 'hover:bg-gray-400/50 hover:border-gray-900 hover:cursor-pointer' : ''} `}
            onClick={() => {
              if ((task.status === 'completed') || (task.status !== 'pending' && (isSuperior || task.assigned_to === userId))) {
                let assig;
                if (!task.is_external) {
                  assig = workOrder.assignments?.find(assig => assig.employee_id === task.assigned_to);
                } else {
                  assig = workOrder.assignments_external?.find(assig => assig.employee_id === task.assigned_to);
                }
                setSelectedTask(task);
                if (assig) {
                  setSelectedAssig(assig);
                }
                setIsModalOpen(true);
              }
            }}>
            {editingTaskId === task.id ? (
              // Edit form
              <form onSubmit={(e) => handleEditFormSubmit(e, task.id)} className="flex flex-col gap-2">
                <textarea
                  value={editTaskDesc}
                  onChange={(e) => setEditTaskDesc(e.target.value)}
                  className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600 overflow-y-auto"
                  rows={4}
                  autoFocus
                />
                <select
                  value={editTaskAssignee}
                  onChange={(e) => setEditTaskAssignee(e.target.value)}
                  className="px-3 py-2 rounded bg-gray-800 text-white border border-gray-600"
                >
                  <option value="">Unassigned</option>
                  {assigneeOptions.map((assignment) => (
                    <option key={assignment.employee_id} value={assignment.employee_id}>
                      {assignment.employee_name}
                    </option>
                  ))}
                </select>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingTaskId(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button type="submit" className="text-green-400 hover:text-green-300">
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </form>
            ) : (<>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-400 font-mono">#{task.task_number}</span>
                  <p className="text-white font-medium whitespace-pre-wrap break-words">{task.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {userId === workOrder.created_by && task.status === 'pending' && workOrder.work_order_type === 'corrective' && (
                    <div className='flex flex-col items-center gap-2'>
                      <button
                        onClick={() => {
                          setEditingTaskId(task.id);
                          setEditTaskDesc(task.description);
                          setEditTaskAssignee(task.assigned_to || '');
                        }}
                        className="text-gray-400 hover:text-blue-400"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDeleteTask(task.id)} className="text-gray-400 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <Badge
                    className={`${task.status === 'completed'
                      ? 'bg-green-900/50 text-green-200'
                      : task.status === 'in_progress'
                        ? 'bg-yellow-900/50 text-yellow-200'
                        : 'bg-gray-700/50 text-gray-400'
                      }`}
                  >
                    {task.status ? task.status.charAt(0).toUpperCase() + task.status.slice(1) : 'Pending'}
                  </Badge>


                  {task.status === 'pending' && (isSuperior || task.assigned_to === userId) && (
                    <button
                      onClick={() => {
                        setSubmittingTaskId(task.id);
                        handleStartTask(task);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submittingTaskId === task.id}
                    >
                      {submittingTaskId === task.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CirclePlay className="w-4 h-4" />
                      )}
                      Start
                    </button>
                  )}
                </div>
              </div>
              {task.assigned_to && (
                <div className="flex items-center gap-2 mt-2 text-sm text-gray-400">
                  <User className="w-3 h-3" />
                  <span>Assigned to: {task.assignee_name || task.assigned_to}</span>
                </div>
              )}
              {task.completed_at && (
                <div className="flex items-center gap-2 mt-2 text-sm text-green-400">
                  <CheckCircle className="w-3 h-3" />
                  <span>Completed: {dateFormat(task.completed_at)}</span>
                </div>
              )}
            </>
            )}
          </div>

        ))}
        {tasks.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tasks.</p>
          </div>
        )}
      </div>
      {/* Modal para crear plan */}
      {isModalOpen && (
        createPortal(
          <ExecutionModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              if (!selectedTask) {
                setSelectedTask(null);
              }
            }}
            onFinish={onFinish}
            task={selectedTask}
            workOrder={workOrder}
            spareParts={spareParts}
            onUpdateTask={handleUpdateTask}
            onUpdateAssig={handleUpdateAssig}
            onUpdateExtAssig={handleUpdateExtAssig}
            assig={selectedAssig}
            documentTypes={documentTypes}
          />, document.body)
      )}
    </div>
  );
};